import { useState, type CSSProperties, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  Heart,
  Footprints,
  BedDouble,
  Activity,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Button } from '@/components/ui'
import {
  StepsChart,
  HeartRateChart,
  SleepChart,
  PeriodSelector,
  WellbeingHero,
} from '@/components/health'
import { ParentSelector } from '@/components/family'
import {
  SectionHeader,
  QuickStatCard,
  ConnectionStatusChip,
  severityIcon,
  severityLabel,
  severityTone,
  avatarTint,
  initialsOf,
  type StatTrend,
} from '@/components/hearth'
import { cn } from '@/lib/utils'
import {
  useAuth,
  useFamily,
  useFamilyMembers,
  useHealthConnections,
  useHealthMetrics,
  useHealthSummary,
  useHealthTrends,
  usePermissions,
  useRoleBasedMemberId,
  useAlerts,
  getPeriodDateRange,
  groupMetricsByDate,
  type HealthPeriod,
} from '@/hooks'
import type {
  Alert,
  FamilyMember,
  HealthConnection,
  HealthSummary,
  HealthTrendsResponse,
  MetricType,
} from '@/types'

/** Staggered Hearth entrance wrapper */
function Appear({ order, children, className }: { order: number; children: ReactNode; className?: string }) {
  return (
    <div className={cn('hearth-appear', className)} style={{ '--stagger': order } as CSSProperties}>
      {children}
    </div>
  )
}

function getSourceLabel(source: HealthConnection['source']) {
  return source === 'APPLE_HEALTH' ? 'Apple Health' : 'Google Health Connect'
}

function formatSleepHours(hours: number) {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

// Helper to extract the average for a metric type from the summary
function averageOf(summary: HealthSummary | undefined, metricType: MetricType): number | null {
  const metric = summary?.metrics_by_type?.[metricType]
  return metric?.average ?? null
}

// Prefer the most recent reading when the summary provides one
function latestOf(summary: HealthSummary | undefined, metricType: MetricType): number | null {
  const metric = summary?.metrics?.find((m) => m.metric_type === metricType)
  return metric?.latest_value ?? null
}

// Map backend trend direction onto the Hearth stat trend
function statTrendOf(
  trends: HealthTrendsResponse | undefined,
  metricType: MetricType
): StatTrend | undefined {
  const trend = trends?.metrics?.[metricType]
  if (!trend) return undefined

  const changePercent = trend.change_percent ?? 0
  const direction =
    trend.trend ??
    (Math.abs(changePercent) < 2 ? 'stable' : changePercent > 0 ? 'improving' : 'declining')

  const mapped = direction === 'improving' ? 'up' : direction === 'declining' ? 'down' : 'stable'
  return {
    direction: mapped,
    label: changePercent ? `${Math.abs(changePercent).toFixed(0)}%` : undefined,
  }
}

const trendArrows: Record<'up' | 'down' | 'stable', { icon: LucideIcon; className: string }> = {
  up: { icon: ArrowUpRight, className: 'text-success-600' },
  // A downward trend is worth a look, never an alarm.
  down: { icon: ArrowDownRight, className: 'text-warning-600' },
  stable: { icon: ArrowRight, className: 'text-gray-400' },
}

interface TrendRowConfig {
  type: MetricType
  label: string
  icon: LucideIcon
  chipClass: string
  format: (value: number) => string
}

const trendRowConfigs: TrendRowConfig[] = [
  {
    type: 'STEPS',
    label: 'Steps',
    icon: Footprints,
    chipClass: 'bg-success-500/10 text-success-500',
    format: (v) => `${Math.round(v).toLocaleString()} steps`,
  },
  {
    type: 'HEART_RATE',
    label: 'Heart rate',
    icon: Heart,
    chipClass: 'bg-accent-500/10 text-accent-500',
    format: (v) => `${Math.round(v)} bpm`,
  },
  {
    type: 'SLEEP_DURATION',
    label: 'Sleep',
    icon: BedDouble,
    chipClass: 'bg-[#7c6fa8]/10 text-[#7c6fa8]',
    format: formatSleepHours,
  },
  {
    type: 'HEART_RATE_RESTING',
    label: 'Resting heart rate',
    icon: Activity,
    chipClass: 'bg-info-500/10 text-info-500',
    format: (v) => `${Math.round(v)} bpm`,
  },
]

function TrendsSection({ trends }: { trends: HealthTrendsResponse | undefined }) {
  const rows = trendRowConfigs
    .map((config) => {
      const trend = trends?.metrics?.[config.type]
      if (!trend) return null
      const changePercent = trend.change_percent ?? 0
      const direction =
        trend.trend ??
        (Math.abs(changePercent) < 2 ? 'stable' : changePercent > 0 ? 'improving' : 'declining')
      const mapped = direction === 'improving' ? 'up' : direction === 'declining' ? 'down' : 'stable'
      return { config, average: trend.average, direction: mapped as 'up' | 'down' | 'stable' }
    })
    .filter((row): row is NonNullable<typeof row> => row !== null)

  return (
    <div className="space-y-3">
      <SectionHeader title="Trends" />
      {rows.length === 0 ? (
        <div className="hearth-card p-5">
          <p className="text-sm text-gray-500">Trends appear after a few days of readings.</p>
        </div>
      ) : (
        <div className="hearth-card divide-y divide-gray-100">
          {rows.map(({ config, average, direction }) => {
            const Icon = config.icon
            const arrow = trendArrows[direction]
            const ArrowIcon = arrow.icon
            return (
              <div key={config.type} className="flex items-center gap-3 p-4">
                <span
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                    config.chipClass
                  )}
                >
                  <Icon className="h-4.5 w-4.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">{config.label}</p>
                  <p className="text-[13px] text-gray-500">
                    Average{' '}
                    <span className="font-rounded font-medium tabular-nums">
                      {config.format(average)}
                    </span>
                  </p>
                </div>
                <ArrowIcon className={cn('h-5 w-5 shrink-0', arrow.className)} />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function HealthAlertsSection({
  alerts,
  onDismiss,
  isDismissing,
}: {
  alerts: Alert[]
  onDismiss: (alert: Alert) => void
  isDismissing: boolean
}) {
  return (
    <div className="space-y-3">
      <SectionHeader title="Worth knowing" />
      <div className="hearth-card divide-y divide-gray-100">
        {alerts.map((alert) => {
          const Icon = severityIcon[alert.severity]
          const tone = severityTone[alert.severity]
          return (
            <div key={alert.id} className="flex items-center gap-3 p-4">
              <span
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                  tone.chipBg
                )}
              >
                <Icon className={cn('h-5 w-5', tone.fg)} />
              </span>
              <Link to={`/alerts?id=${alert.id}`} className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{alert.title}</p>
                <p className="mt-0.5 text-[13px] text-gray-500">
                  {severityLabel[alert.severity]} ·{' '}
                  {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
                </p>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                disabled={isDismissing}
                onClick={() => onDismiss(alert)}
              >
                Dismiss
              </Button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function FamilySection({
  parents,
  connections,
}: {
  parents: FamilyMember[]
  connections: HealthConnection[]
}) {
  return (
    <div className="space-y-3">
      <SectionHeader title="Family" />
      {parents.length === 0 ? (
        <div className="hearth-card p-5">
          <p className="text-sm text-gray-500">
            Invite a parent to start seeing their health here.
          </p>
        </div>
      ) : (
        <div className="hearth-card divide-y divide-gray-100">
          {parents.map((parent) => {
            const name = parent.user.full_name
            const tint = avatarTint(name)
            const parentConnections = connections.filter(
              (connection) => connection.parent_user === parent.user.id
            )
            const status = parentConnections.some((c) => c.status === 'ERROR')
              ? { label: 'Needs attention', dot: 'bg-warning-500' }
              : parentConnections.some((c) => c.status === 'CONNECTED')
                ? { label: 'Connected', dot: 'bg-success-500' }
                : { label: 'Not connected yet', dot: 'bg-gray-300' }
            const sources = parentConnections
              .map((connection) => getSourceLabel(connection.source))
              .join(', ')
            return (
              <Link
                key={parent.id}
                to={`/health/member/${parent.id}`}
                className="hearth-press flex items-center gap-3 p-4"
              >
                <span
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                    tint.bg,
                    tint.fg
                  )}
                >
                  {initialsOf(name)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">{name}</p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-[13px] text-gray-500">
                    <span className={cn('h-1.5 w-1.5 rounded-full', status.dot)} />
                    {status.label}
                    {sources && ` · ${sources}`}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

function ConnectPromptState({ name, isSelf }: { name: string; isSelf: boolean }) {
  return (
    <div className="hearth-card-floating flex flex-col items-center px-6 py-10 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
        <Heart className="h-8 w-8 text-primary-600" />
      </div>
      <h2 className="mt-4 font-serif text-xl font-semibold text-gray-900">
        Let's get connected
      </h2>
      <p className="mt-2 max-w-md text-[15px] text-gray-500">
        {isSelf
          ? 'Turn on health sharing from the FamSteady app on your phone, and your readings will gently appear here as they sync.'
          : `Once ${name} turns on health sharing from the FamSteady app on their phone, readings will gently appear here as they sync.`}
      </p>
    </div>
  )
}

export function HealthOverviewPage() {
  const [period, setPeriod] = useState<HealthPeriod>('7d')
  const { user } = useAuth()
  const { selectedFamilyId } = useFamily()
  const { members } = useFamilyMembers(selectedFamilyId)
  const { connections } = useHealthConnections(selectedFamilyId)
  const { parentUserId, shouldShowMemberSelector } = useRoleBasedMemberId()
  const { isParent } = usePermissions()

  // Fetch health alerts
  const { alerts, resolveAlert, isResolving } = useAlerts(selectedFamilyId, {
    status: 'UNREAD',
    alert_type: 'HEALTH',
    page_size: 5,
  })

  // Fetch real data with user ID filtering (health APIs require parent_user_id)
  const { data: summary, isLoading: isLoadingSummary } = useHealthSummary(selectedFamilyId, {
    parent_user_id: parentUserId,
    period,
  })

  const { data: trends } = useHealthTrends(selectedFamilyId, {
    parent_user_id: parentUserId,
    period,
  })

  const dateRange = getPeriodDateRange(period)
  const { data: stepsData, isLoading: isStepsLoading } = useHealthMetrics(selectedFamilyId, {
    parent_user_id: parentUserId,
    metric_type: 'STEPS',
    start_date: dateRange.start_date,
    end_date: dateRange.end_date,
    page_size: 500,
  })
  const { data: heartRateData, isLoading: isHeartRateLoading } = useHealthMetrics(selectedFamilyId, {
    parent_user_id: parentUserId,
    metric_type: 'HEART_RATE',
    start_date: dateRange.start_date,
    end_date: dateRange.end_date,
    page_size: 500,
  })
  const { data: sleepData, isLoading: isSleepLoading } = useHealthMetrics(selectedFamilyId, {
    parent_user_id: parentUserId,
    metric_type: 'SLEEP_DURATION',
    start_date: dateRange.start_date,
    end_date: dateRange.end_date,
    page_size: 500,
  })

  const parentMembers = members.filter((m) => m.role === 'PARENT')
  const hasAlerts = alerts.length > 0

  // Focus person: the parent whose data this page is showing
  const focusMember = members.find((m) => m.user.id === parentUserId)
  const focusFullName = focusMember?.user.full_name ?? user?.full_name ?? 'your family member'
  const focusFirstName = focusFullName.split(' ')[0]
  const focusConnections = connections.filter(
    (connection) => connection.parent_user === parentUserId
  )

  // Check if we have any actual data
  const hasData = !!summary?.metrics_by_type && Object.keys(summary.metrics_by_type).length > 0
  const showConnectPrompt =
    !isLoadingSummary && focusConnections.length === 0 && !hasData && !!parentUserId

  const isChartsLoading = isStepsLoading || isHeartRateLoading || isSleepLoading

  // Metric values: prefer the latest reading, fall back to the period average
  const stepsLatest = latestOf(summary, 'STEPS')
  const stepsValue = stepsLatest ?? averageOf(summary, 'STEPS')
  const heartLatest = latestOf(summary, 'HEART_RATE')
  const heartValue = heartLatest ?? averageOf(summary, 'HEART_RATE')
  const sleepLatest = latestOf(summary, 'SLEEP_DURATION')
  const sleepValue = sleepLatest ?? averageOf(summary, 'SLEEP_DURATION')
  const restingValue =
    latestOf(summary, 'HEART_RATE_RESTING') ?? averageOf(summary, 'HEART_RATE_RESTING')

  const stepsChartData = groupMetricsByDate(
    (stepsData?.data ?? []).map((m) => ({ timestamp: m.timestamp, value: m.value }))
  )
    .map((d) => ({ date: d.date, value: Math.round(d.value) }))
    .sort((a, b) => a.date.localeCompare(b.date))

  const heartRateChartData = groupMetricsByDate(
    (heartRateData?.data ?? []).map((m) => ({ timestamp: m.timestamp, value: m.value }))
  )
    .map((d) => ({
      date: d.date,
      value: Math.round(d.value),
      min: Math.round(d.min),
      max: Math.round(d.max),
    }))
    .sort((a, b) => a.date.localeCompare(b.date))

  const sleepChartData = groupMetricsByDate(
    (sleepData?.data ?? []).map((m) => ({ timestamp: m.timestamp, value: m.value }))
  )
    .map((d) => ({
      date: d.date,
      value: parseFloat(d.value.toFixed(1)),
      quality: 80,
    }))
    .sort((a, b) => a.date.localeCompare(b.date))

  const handleDismissAlert = async (alert: Alert) => {
    await resolveAlert({
      alertId: alert.id,
      data: { resolution: 'NO_ACTION_NEEDED' },
    })
  }

  let stagger = 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <Appear order={stagger++}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-serif text-2xl font-semibold text-gray-900">Health</h1>
            <p className="mt-0.5 text-[15px] text-gray-500">
              {hasAlerts
                ? 'A few things are worth a look'
                : `${isParent ? 'Your' : `${focusFirstName}'s`} vitals and activity, at a glance`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {shouldShowMemberSelector && <ParentSelector />}
            <PeriodSelector value={period} onChange={setPeriod} />
          </div>
        </div>
      </Appear>

      {/* No member to look at yet (caregiver with no parents) */}
      {!parentUserId && !isParent && (
        <Appear order={stagger++}>
          <FamilySection parents={parentMembers} connections={connections} />
        </Appear>
      )}

      {parentUserId && (
        <>
          {/* Wellbeing hero / connect prompt */}
          <Appear order={stagger++}>
            {showConnectPrompt ? (
              <ConnectPromptState name={focusFirstName} isSelf={isParent} />
            ) : (
              <WellbeingHero
                name={focusFirstName}
                isSelf={isParent}
                score={summary?.health_score}
                insight={summary?.insights?.[0]}
              />
            )}
          </Appear>

          {/* Connection status */}
          {focusConnections.length > 0 && (
            <Appear order={stagger++} className="flex flex-wrap gap-2">
              {focusConnections.map((connection) => (
                <ConnectionStatusChip
                  key={connection.id}
                  title={getSourceLabel(connection.source)}
                  state={connection.status === 'CONNECTED' ? 'connected' : 'disconnected'}
                  lastUpdated={connection.last_sync_at}
                />
              ))}
            </Appear>
          )}

          {!showConnectPrompt && (
            <>
              {/* Metric grid */}
              <Appear order={stagger++}>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <QuickStatCard
                    icon={Footprints}
                    iconClassName="bg-success-500/10 text-success-500"
                    title="Steps"
                    value={stepsValue != null ? Math.round(stepsValue).toLocaleString() : '--'}
                    subtitle={stepsLatest != null ? 'Steps today' : 'Daily average'}
                    trend={statTrendOf(trends, 'STEPS')}
                    sparklineData={stepsChartData.map((d) => d.value)}
                    sparklineColorClass="text-success-500"
                  />
                  <QuickStatCard
                    icon={Heart}
                    iconClassName="bg-accent-500/10 text-accent-500"
                    title="Heart rate"
                    value={heartValue != null ? `${Math.round(heartValue)} bpm` : '--'}
                    subtitle={heartLatest != null ? 'Latest reading' : 'Average'}
                    trend={statTrendOf(trends, 'HEART_RATE')}
                    sparklineData={heartRateChartData.map((d) => d.value)}
                    sparklineColorClass="text-accent-500"
                  />
                  <QuickStatCard
                    icon={BedDouble}
                    iconClassName="bg-[#7c6fa8]/10 text-[#7c6fa8]"
                    title="Sleep"
                    value={sleepValue != null ? formatSleepHours(sleepValue) : '--'}
                    subtitle={sleepLatest != null ? 'Last night' : 'Nightly average'}
                    trend={statTrendOf(trends, 'SLEEP_DURATION')}
                    sparklineData={sleepChartData.map((d) => d.value)}
                    sparklineColorClass="text-[#7c6fa8]"
                  />
                  <QuickStatCard
                    icon={Activity}
                    iconClassName="bg-info-500/10 text-info-500"
                    title="Resting heart rate"
                    value={restingValue != null ? `${Math.round(restingValue)} bpm` : '--'}
                    subtitle="At rest"
                    trend={statTrendOf(trends, 'HEART_RATE_RESTING')}
                  />
                </div>
              </Appear>

              {/* Trends */}
              <Appear order={stagger++}>
                <TrendsSection trends={trends} />
              </Appear>

              {/* Charts */}
              <Appear order={stagger++}>
                <div className="space-y-3">
                  <SectionHeader title="Recent readings" />
                  <div className="grid gap-4 lg:grid-cols-2">
                    <StepsChart data={stepsChartData} goal={10000} isLoading={isChartsLoading} />
                    <HeartRateChart
                      data={heartRateChartData}
                      restingHeartRate={restingValue != null ? Math.round(restingValue) : undefined}
                      isLoading={isChartsLoading}
                    />
                  </div>
                  <SleepChart data={sleepChartData} goalHours={8} isLoading={isChartsLoading} />
                </div>
              </Appear>
            </>
          )}
        </>
      )}

      {/* Health alerts */}
      {hasAlerts && (
        <Appear order={stagger++}>
          <HealthAlertsSection
            alerts={alerts}
            onDismiss={handleDismissAlert}
            isDismissing={isResolving}
          />
        </Appear>
      )}

      {/* Family members (caregivers) */}
      {!isParent && parentUserId && parentMembers.length > 0 && (
        <Appear order={stagger++}>
          <FamilySection parents={parentMembers} connections={connections} />
        </Appear>
      )}
    </div>
  )
}
